/**
 * IngestPage — Upload assets into a project with auto-routing.
 *
 * Files are auto-sorted by type (Video, Audio, Images, Documents, Other)
 * into date-based folders (YYYY-MM-DD). No manual folder selection needed.
 */

import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, CheckCircle, X, FileImage, FileVideo, Music, FileText, File, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import FileUpload from '../components/Upload/FileUpload'
import { assetApi } from '../services/assetFolderApi'
import { normalizeErrorMessage } from '../utils/dataValidator'

interface UploadedAsset {
  id: number
  filename: string
  file_size: number
  file_type?: string
  asset_type: string
  folder_id?: number
  folder_path?: string | null
  needs_transcoding?: boolean
  transcoding_reason?: string | null
}

/** Group uploaded assets by folder_path for summary display. */
function groupByFolder(assets: UploadedAsset[]): Record<string, UploadedAsset[]> {
  const groups: Record<string, UploadedAsset[]> = {}
  for (const a of assets) {
    const key = a.folder_path || 'Unsorted'
    ;(groups[key] ??= []).push(a)
  }
  return groups
}

/** Icon for a type-folder name. */
function typeIcon(folderPath: string) {
  const lower = folderPath.toLowerCase()
  if (lower.includes('video')) return <FileVideo className="w-4 h-4 text-[var(--rq-blue)]" />
  if (lower.includes('audio')) return <Music className="w-4 h-4 text-amber-400" />
  if (lower.includes('images')) return <FileImage className="w-4 h-4 text-pink-400" />
  if (lower.includes('documents')) return <FileText className="w-4 h-4 text-gray-400" />
  return <File className="w-4 h-4 text-[var(--rq-text-soft)]" />
}

export default function IngestPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [uploadedAssets, setUploadedAssets] = useState<UploadedAsset[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadSpeedMBps, setUploadSpeedMBps] = useState(0)
  const [uploadDate, setUploadDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [showFormats, setShowFormats] = useState(false)

  const handleFilesUpload = async (files: File[]) => {
    if (!projectId) {
      toast.error('No workspace selected')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)
    setUploadSpeedMBps(0)

    try {
      const result = await assetApi.uploadDirect(projectId, files, {
        uploadDate,
        onProgress: setUploadProgress,
        onSpeed: setUploadSpeedMBps,
      })

      setUploadedAssets(prev => [...prev, ...result.files])
      toast.success(`Uploaded ${result.files.length} file(s)`)
    } catch (err) {
      toast.error(`Upload failed: ${normalizeErrorMessage(err, 'Unknown error')}`)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      setUploadSpeedMBps(0)
    }
  }

  const removeAsset = async (id: number) => {
    try {
      await assetApi.delete(id)
      setUploadedAssets(prev => prev.filter(a => a.id !== id))
    } catch {
      toast.error('Failed to remove asset')
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  const grouped = groupByFolder(uploadedAssets)

  return (
    <div className="rq-page max-w-4xl">
      {/* Back link */}
      {projectId && (
        <Link
          to={`/projects/${projectId}`}
          className="inline-flex items-center gap-1 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Workspace
        </Link>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="rq-title mb-2">Upload Assets</h1>
        <p className="text-[var(--rq-text-soft)]">
          Drop your files — they auto-sort into Video, Audio, Images, Documents, or Other
        </p>
      </div>

      {/* Date picker */}
      <div className="rq-card p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs text-[var(--rq-text-soft)] mb-1">Upload date</label>
            <input
              type="date"
              value={uploadDate}
              onChange={e => setUploadDate(e.target.value)}
              className="rq-input-dark text-sm w-full max-w-[200px]"
            />
          </div>
          <p className="text-xs text-[var(--rq-text-soft)] mt-4">
            Files auto-sort by type into folders under this date
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div className="mb-8">
        <FileUpload
          onFilesUpload={handleFilesUpload}
          isUploading={isUploading}
          maxFiles={500}
          acceptAll
        />

        {/* Upload progress */}
        {isUploading && uploadProgress > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-[var(--rq-text-soft)] mb-1">
              <span>Uploading files...</span>
              <span className="tabular-nums">
                {uploadSpeedMBps > 0 && (
                  <span className="text-[var(--rq-text)] font-medium mr-3">
                    {uploadSpeedMBps.toFixed(1)} MB/s
                  </span>
                )}
                {uploadProgress}%
              </span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div
                className="bg-[var(--btn)] h-2 rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Uploaded assets grouped by type folder */}
      {uploadedAssets.length > 0 && (
        <div className="rq-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--rq-text)]">
              Uploaded ({uploadedAssets.length})
            </h3>
            <div className="flex items-center text-emerald-400 text-sm">
              <CheckCircle className="w-4 h-4 mr-1" /> Ready
            </div>
          </div>

          {/* Per-folder groups */}
          <div className="space-y-4">
            {Object.entries(grouped).map(([folderPath, assets]) => (
              <div key={folderPath}>
                <div className="flex items-center gap-2 mb-2">
                  {typeIcon(folderPath)}
                  <span className="text-xs font-medium text-[var(--rq-text-soft)]">
                    {folderPath} ({assets.length})
                  </span>
                </div>
                <div className="space-y-2 ml-6">
                  {assets.map(asset => (
                    <div
                      key={asset.id}
                      className="flex items-center justify-between p-2.5 bg-black/25 border border-white/10 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[var(--rq-text)] truncate">{asset.filename}</p>
                          <p className="text-xs text-[var(--rq-text-soft)]">
                            {formatFileSize(asset.file_size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeAsset(asset.id)}
                        className="p-1 text-[var(--rq-text-soft)] hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Done button */}
          <div className="mt-6 pt-4 border-t border-white/10 flex gap-3">
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              className="flex-1 rq-btn-primary py-2.5 justify-center"
            >
              Back to Workspace
            </button>
          </div>
        </div>
      )}

      {/* Supported formats info */}
      <div className="mt-6 text-center">
        <button
          onClick={() => setShowFormats(!showFormats)}
          className="inline-flex items-center gap-1 text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] text-xs transition-colors"
        >
          <Info className="w-3 h-3" /> Supported formats
        </button>
        {showFormats && (
          <div className="mt-3 rq-card p-4 text-sm text-[var(--rq-text-soft)] max-w-md mx-auto">
            <p><strong className="text-[var(--rq-text)]">Video:</strong> MP4, MOV, AVI, WMV, FLV, WebM, MKV, MXF, R3D, BRAW</p>
            <p><strong className="text-[var(--rq-text)]">Audio:</strong> MP3, WAV, OGG, FLAC, AAC, M4A, AIFF</p>
            <p><strong className="text-[var(--rq-text)]">Images:</strong> JPG, PNG, GIF, BMP, TIFF, WebP, EXR, DPX, PSD</p>
            <p><strong className="text-[var(--rq-text)]">Documents:</strong> PDF, TXT, DOC, DOCX, RTF, CSV, XLS, XLSX</p>
            <p className="mt-2 text-xs">Unknown file types go to Other. Max 2GB per file.</p>
          </div>
        )}
      </div>
    </div>
  )
}
